import {
    IonBackButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonContent,
    IonHeader,
    IonIcon,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    IonLoading,
    IonPage,
    IonRefresher,
    IonRefresherContent,
    IonTitle,
    IonToast,
    IonToolbar,
    useIonViewDidEnter
} from '@ionic/react';
import { arrowBack, documentTextOutline, receiptOutline } from 'ionicons/icons';
import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { MessageContext } from '../../../../../contexts/MessageContext';
import { fiscalListarNFCe, requestService } from '../../../../../Utils/Services';
import './FiscalHistorico.css';

type Props = {
    abrirMenu: boolean;
};

const FiscalHistorico: React.FC<Props> = (props: Props) => {
    const history = useHistory();
    const { loja } = useContext(MessageContext);
    const { esconderMenu, setEsconderMenu } = useContext(MessageContext);
    const { nomeMenu, setNomeMenu } = useContext(MessageContext);

    const [notas, setNotas] = useState<any[]>([]);
    const [showLoading, setShowLoading] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [textToast, setTextToast] = useState("");
    const [searchText, setSearchText] = useState("");
    const [lastKey, setLastKey] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(true);

    const [acessos] = useState<any>(JSON.parse(window.localStorage.getItem("acessos") || '{}'));

    useIonViewDidEnter(() => {
        if (acessos?.fiscal != 'leitura' && acessos?.fiscal != 'edicao') {
            history.replace('/empresa/home');
            return;
        }
        setEsconderMenu(true);
        setNomeMenu("");
        carregarNotas(true);
    });

    function carregarNotas(reset: boolean = false) {
        if (!loja) return;

        if (reset) {
            setShowLoading(true);
            setNotas([]);
            setLastKey(null);
        }

        let url = fiscalListarNFCe.url.replace("{empresaId}", loja.replaceAll(/#/g, '%23'));
        url += "?limite=50";
        if (!reset && lastKey) {
            url += "&lastKey=" + encodeURIComponent(lastKey);
        }

        requestService(
            url,
            { method: fiscalListarNFCe.method, credentials: 'include' },
            history,
            (response: any) => {
                setShowLoading(false);
                const items = response.dados || response.items || response.notas || [];
                if (reset) {
                    setNotas(items);
                } else {
                    setNotas(prev => [...prev, ...items]);
                }
                setLastKey(response.lastKey || null);
                setHasMore(!!response.lastKey);
            },
            (error: any) => {
                setShowLoading(false);
                setTextToast(error.message || "Erro ao carregar notas.");
                setShowToast(true);
            }
        );
    }

    function doRefresh(event: any) {
        carregarNotas(true);
        setTimeout(() => event.detail.complete(), 500);
    }

    function loadMore(event: any) {
        if (hasMore) {
            carregarNotas(false);
        }
        setTimeout(() => event.target.complete(), 500);
    }

    function formatarData(data: string) {
        if (!data) return "";
        return new Date(data).toLocaleDateString('pt-BR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    }

    function formatarValor(valor: number) {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor || 0);
    }

    function getStatusClass(status: string) {
        if (!status) return "";
        const s = status.toUpperCase();
        if (s.includes("AUTORIZADA")) return "autorizada";
        if (s.includes("CANCELADA")) return "cancelada";
        if (s.includes("INUTILIZADA")) return "inutilizada";
        return "";
    }

    function abrirDetalhes(nota: any) {
        history.push('/empresa/fiscaldetalhes', { nota });
    }

    const notasFiltradas = notas.filter(nota => {
        if (!searchText) return true;
        const search = searchText.toLowerCase();
        return (
            (nota.numero && String(nota.numero).includes(search)) ||
            (nota.chave && nota.chave.includes(search)) ||
            (nota.status && nota.status.toLowerCase().includes(search))
        );
    });

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar color="primary">
                    <IonButtons slot="start">
                        <IonBackButton icon={arrowBack} text='' />
                    </IonButtons>
                    <IonTitle slot="start">Histórico de Notas</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent className="fiscal-historico-container" color="light">
                <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
                    <IonRefresherContent />
                </IonRefresher>

                {/* <div className="fiscal-filtros">
                    <IonSearchbar
                        value={searchText}
                        onIonChange={(e) => setSearchText(e.detail.value || "")}
                        placeholder="Buscar por número, chave ou status"
                        debounce={300}
                    />
                </div> */}

                {notasFiltradas.length === 0 && !showLoading ? (
                    <div className="fiscal-empty-state">
                        <IonIcon icon={receiptOutline} />
                        <h3>Nenhuma nota encontrada</h3>
                        <p>As notas emitidas aparecerão aqui</p>
                    </div>
                ) : (
                    notasFiltradas.map((nota: any, index: number) => (
                        <IonCard key={nota.id || nota.nfceId || index} className="fiscal-card-nota" onClick={() => abrirDetalhes(nota)}>
                            <IonCardContent>
                                <div className="fiscal-nota-header">
                                    <span className="fiscal-nota-numero">
                                        <IonIcon icon={documentTextOutline} style={{ marginRight: "6px", verticalAlign: "middle" }} />
                                        NFC-e nº {nota.numero || "—"}
                                    </span>
                                    <span className={`fiscal-nota-status ${getStatusClass(nota.status)}`}>
                                        {nota.status || "—"}
                                    </span>
                                </div>
                                <div className="fiscal-nota-info">
                                    <span className="fiscal-nota-data">{formatarData(nota.dataEmissao || nota.createdAt)}</span>
                                    <span className="fiscal-nota-valor">{formatarValor(nota.valorTotal)}</span>
                                </div>
                            </IonCardContent>
                        </IonCard>
                    ))
                )}

                <IonInfiniteScroll threshold="100px" disabled={!hasMore} onIonInfinite={loadMore}>
                    <IonInfiniteScrollContent loadingText="Carregando mais notas..." />
                </IonInfiniteScroll>

                <IonLoading isOpen={showLoading} message="Carregando notas..." />

                <IonToast
                    isOpen={showToast}
                    position="top"
                    onDidDismiss={() => setShowToast(false)}
                    message={textToast}
                    duration={3000}
                    color="danger"
                />
            </IonContent>
        </IonPage>
    );
};

export default FiscalHistorico;
